import type { HomePageUser, ProfileData } from "gsintegrations";
import { axiosClient } from "../api/axiosClient";

export type OnboardingIntroStatus = "PENDING" | "DISMISSED" | "COMPLETED";

export interface OnboardingStatePayload {
  introStatus: OnboardingIntroStatus;
  introViewedAt: string | null;
  introDismissedAt: string | null;
  introCompletedAt: string | null;
  stagingWarningAcknowledgedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export type HomePageUserProfile = HomePageUser & {
  message: string;
  profiles?: Array<{
    mediumId: string;
    terreiroId: string | null;
    status: string;
    isPersonal: boolean;
  }>;
  activeProfile?: {
    mediumId: string;
    terreiroId: string | null;
    status: string;
    isPersonal: boolean;
  } | null;
  roles?: string[];
  onboardingState?: OnboardingStatePayload | null;
};

export type OnboardingStateEvent =
  | "viewed"
  | "dismissed"
  | "completed"
  | "staging_warning_acknowledged";

export interface GetProfileOptions {
  includeUser?: boolean;
  showProfileId?: boolean;
  includeAddress?: boolean;
  includeContact?: boolean;
  includeMedium?: boolean;
  mediumId?: string;
}

export const profileQueryKeys = {
  profile: ["profile"] as const,
};

export const profileRepository = {
  async getProfile(
    options: GetProfileOptions = {},
  ): Promise<HomePageUserProfile> {
    // Convert boolean options to "1"/"0" strings for query params
    const params = {
      includeUser: options.includeUser ? "1" : "0",
      showProfileId: options.showProfileId ? "1" : "0",
      includeAddress: options.includeAddress ? "1" : "0",
      includeContact: options.includeContact ? "1" : "0",
      includeMedium: options.includeMedium ? "1" : "0",
      ...(options.mediumId ? { mediumId: options.mediumId } : {}),
    };

    try {
      const response = await axiosClient.get<HomePageUserProfile>(
        `/profile/me`,
        {
          params,
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching profile:", error);
      throw error;
    }
  },
  async updateProfile(
    profileData: Partial<ProfileData>,
  ): Promise<HomePageUserProfile> {
    try {
      const response = await axiosClient.put<HomePageUserProfile>(
        `/profile`,
        profileData,
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  },

  async uploadAvatar(
    file: File | Blob,
    filename?: string,
  ): Promise<HomePageUserProfile> {
    const form = new FormData();
    // Use provided filename when available (File constructor may supply name)
    form.append(
      "avatar",
      file as Blob,
      filename ?? (file instanceof File ? file.name : "avatar"),
    );

    try {
      const response = await axiosClient.patch<HomePageUserProfile>(
        `/profile/avatar/upload`,
        form,
        {
          // Let the browser set the proper multipart boundary; including header is optional.
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading avatar:", error);
      throw error;
    }
  },

  async updateOnboardingState(
    event: OnboardingStateEvent,
  ): Promise<OnboardingStatePayload> {
    const response = await axiosClient.patch<{
      onboardingState: OnboardingStatePayload;
    }>("/profile/onboarding-state", { event }, { withCredentials: true });

    return response.data.onboardingState;
  },
};
